import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { ICurrentPhaseInfo } from 'src/app/core/interfaces';
import { IMySalesContactRequest, SalesContactService } from 'src/app/core/services/sales-contact.service';

/**
 * Global renewal banner.
 *
 * Mounted on the authenticated layout above the `<router-outlet>`. Shows
 * a yellow warning when the user's phase expires within 30 days and a red
 * banner when it has expired. Clicking "Request renewal" opens the shared
 * `<app-sales-contact-modal>` (the unified Sales-Contact + Phase-Renewal
 * flow). On submission the SU triages the request from the Sales Requests
 * page and can Approve (which auto-applies the next Phase) or Decline.
 *
 * `pendingRequest` is populated via `GET /api/sales-contact-requests/mine`
 * on init — when non-null, the CTA flips to a disabled "Renewal request
 * pending" label so the user can't double-submit.
 */
@Component({
  standalone: false,
  selector: 'app-phase-renewal-banner',
  templateUrl: './phase-renewal-banner.component.html',
  styleUrls: ['./phase-renewal-banner.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PhaseRenewalBannerComponent implements OnInit, OnChanges, OnDestroy {
  /** Cached current-phase snapshot from the profile; banner visibility branches on this. */
  @Input() currentPhase: ICurrentPhaseInfo | null | undefined = null;

  private destroy$ = new Subject<void>();

  /** Most recent unresolved sales-contact request from the server; null when none. */
  pendingRequest: IMySalesContactRequest | null = null;

  /** Drives the embedded `<app-sales-contact-modal>`'s [visible] input. */
  showSalesModal = false;

  constructor(
    private salesContactService: SalesContactService,
    private messageService: MessageService,
    private translate: TranslateService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.loadMine();
  }

  /**
   * Profile may not resolve until after init (layout fetches it async);
   * once `currentPhase` flips to a truthy value we re-check the pending
   * request so the banner CTA renders with the correct state.
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['currentPhase'] && this.currentPhase && !this.pendingRequest) {
      this.loadMine();
    }
  }

  private loadMine(): void {
    this.salesContactService
      .getMine()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          this.pendingRequest = response ?? null;
          this.cdr.markForCheck();
        },
        error: () => {
          // Silent — the banner is a soft feature and any network error
          // shouldn't block the rest of the app from rendering.
          this.pendingRequest = null;
          this.cdr.markForCheck();
        },
      });
  }

  /**
   * @description True when the banner should render: the profile reports a
   * phase that has either expired or is within 30 days of expiring.
   */
  get shouldShow(): boolean {
    if (!this.currentPhase) return false;
    return this.currentPhase.isExpired || this.currentPhase.daysRemaining <= 30;
  }

  /** True when the banner is in its red "expired" variant. */
  get isExpired(): boolean {
    return !!this.currentPhase?.isExpired;
  }

  onRequestRenewal(): void {
    this.showSalesModal = true;
  }

  onSalesModalClosed(): void {
    this.showSalesModal = false;
    this.cdr.markForCheck();
  }

  onSalesRequestSubmitted(): void {
    this.showSalesModal = false;
    this.messageService.add({
      severity: 'success',
      detail: this.translate.instant('shared.phaseRenewal.dialog.submittedToast'),
      life: 3000,
    });
    // Re-check so the CTA flips to the pending badge immediately.
    this.loadMine();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
